import React, {useCallback, useMemo} from 'react';
import {Box} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useGetTerritoireQuery} from '../api.js';
import {indexer} from '../selectors/index.js';
import {useCollecte} from '../collecte.js';
import Coquille from '../components/Coquille.js';
import {declassesProfil, defautsParAge, derives, effetLavage, mentionSignal, recidiveImpossibles} from '../selectors/analyses.js';
import CarteVariation from '../components/analyses/CarteVariation.js';
import CarteAge from '../components/analyses/CarteAge.js';
import CarteRecidive from '../components/analyses/CarteRecidive.js';
import {CarteDeclasses, CarteDerive, CarteEffetLavage} from '../components/analyses/CartesSimulees.js';
import {CarteAccessibilite, CarteDetours, CarteMaillage, CarteUsage, CarteVolPied} from '../components/analyses/CartesPopulation.js';
import COMMUNES from '../components/carte/communes-nantes-metropole.geojson.js';
import {accessibiliteParCommune, centreCommune, comptageNonRattaches, detoursAPied, ecartVolOiseauPied, maillageEtUsage, trousDeMaillage} from '../selectors/population.js';
import {cleCommune} from '../selectors/index.js';
import {MAIN_SX} from '../components/dashboard/DashEtats.js';
import {carteSx, sousTitreSx, titreSectionSx} from '../components/dashboard/dashStyles.js';
import {requeteCarte} from '../components/dashboard/dashboard.selectors.js';

const aujourdhuiLocal = () => new Date().toLocaleDateString('sv-SE'); // Y-m-d, fuseau local

// PIL-01 lot 10 : onglet « Analyses ». Une carte par enseignement, chacune portée par une fonction pure de
// selectors/analyses.js. Les cartes simulées ne s'affichent que bouton Collecte à 1.

const GRILLE_SX = {display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '16px', alignItems: 'stretch'};

function Encart({children}) {
    return (
        <Box role="note" sx={{...carteSx, p: '16px 20px', fontSize: 14, color: '#4A525C', backgroundColor: '#F6F8F4'}}>{children}</Box>
    );
}

export default function Analyses() {
    const {slug} = useParams();
    const {data, isLoading, isError} = useGetTerritoireQuery(slug);
    const [collecte] = useCollecte();
    const aujourdhui = useMemo(aujourdhuiLocal, []);
    const index = useMemo(() => (data ? indexer(data, {collecte}) : null), [data, collecte]);
    // Lien vers la carte : tout le parc, sans borne de début, jusqu'à aujourd'hui, plus la surcharge propre à chaque analyse.
    const lien = useCallback((surcharge) => {
        const patrimoines = [...index.patrimoineById.keys()].sort((a, b) => a - b);
        return `/pilotage/${slug}/carte?${requeteCarte(index, aujourdhui, {patrimoines, du: '', au: aujourdhui}, surcharge)}`;
    }, [index, aujourdhui, slug]);
    const reel = useMemo(() => (index ? {recidive: recidiveImpossibles(index), age: defautsParAge(index), maillage: trousDeMaillage(index.carreaux, index.pavs, COMMUNES, {chalandise: index.chalandise}), accessibilite: accessibiliteParCommune(index, index.carreaux, COMMUNES), volPied: ecartVolOiseauPied(index, index.carreaux, COMMUNES), nonRattaches: comptageNonRattaches(index), detours: detoursAPied(index, COMMUNES)} : null), [index]);
    // D5 : carte centrée sur la commune (zoom 13), calque Densité actif ; le filtre commune n'est pas posé (on veut voir les voisines).
    const lienCommune = useCallback((nom) => {
        const c = centreCommune(nom, COMMUNES);
        return lien({densite: true, ...(c ? {z: 13, c} : {})});
    }, [lien]);
    // Cartes simulées : calculées seulement bouton Collecte à 1 (sinon l'index ne contient aucune collecte).
    const simule = useMemo(() => (index && collecte ? {effet: effetLavage(index), derive: derives(index), declasses: declassesProfil(index), usage: maillageEtUsage(index)} : null), [index, collecte]);

    let corps;
    if (isLoading || (!index && !isError)) {
        corps = <Box role="status" sx={sousTitreSx}>Chargement des analyses…</Box>;
    } else if (isError || !index) {
        corps = <Box role="alert" sx={{p: '32px', textAlign: 'center', color: '#B22222', fontWeight: 700}}>Territoire indisponible</Box>;
    } else {
        corps = (
            <>
                {!collecte && <Encart>Activez la collecte pour voir les analyses simulées.</Encart>}
                <Box sx={GRILLE_SX}>
                    {simule && (
                        <CarteVariation index={index} lien={lien} signal={[mentionSignal(data, 'condamnee', index), mentionSignal(data, 'defautBloquant', index)].filter(Boolean).join(' ') || null}/>
                    )}
                    <CarteRecidive resultat={reel.recidive} slug={slug} lienCarte={lien({acte: 'lavage', etats: ['imp']})}/>
                    <CarteAge resultat={reel.age} lienCarte={lien({acte: 'maint', etats: ['pb']})}/>
                    <CarteAccessibilite resultat={reel.accessibilite} lienCommune={lienCommune} lienCarte={lien({densite: true})}/>
                    <CarteMaillage resultat={reel.maillage} lienCommune={lienCommune} chalandise={index.chalandise}/>
                    <CarteVolPied resultat={reel.volPied} lienCommune={lienCommune} nonRattaches={reel.nonRattaches}/>
                    <CarteDetours resultat={reel.detours} slug={slug} lienCommune={lienCommune}/>
                    {simule && (
                        <>
                            <CarteEffetLavage resultat={simule.effet} signal={mentionSignal(data, 'effetLavage', index)} lienCarte={lien({acte: 'collecte', style: 'tonnage'})}/>
                            <CarteDerive
                                resultat={simule.derive}
                                signal={mentionSignal(data, 'derive', index)}
                                lienCarte={lien({acte: 'collecte', style: 'tonnage', ...(simule.derive.circuits[0] ? {patrimoines: [simule.derive.circuits[0].patrimoineId]} : {})})}
                            />
                            <CarteUsage resultat={simule.usage} slug={slug} lienCarte={lien({acte: 'collecte', style: 'chalandise'})}/>
                            <CarteDeclasses resultat={simule.declasses} signal={mentionSignal(data, 'declasses', index)} lienCarte={lien({acte: 'collecte', style: 'abribacs', etats: ['imp']})}/>
                        </>
                    )}
                </Box>
            </>
        );
    }

    return (
        <Coquille slug={slug} onglet="analyses" index={index}>
            <Box sx={{...MAIN_SX, p: 0, maxWidth: 1376}}>
                <Box sx={{display: 'flex', alignItems: 'baseline', gap: '12px'}}>
                    <Box component="h1" sx={{...titreSectionSx, fontSize: 14}}>Analyses</Box>
                    <Box component="span" sx={sousTitreSx}>Ce que les données permettent de constater</Box>
                </Box>
                {corps}
            </Box>
        </Coquille>
    );
}
